import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Search, ChevronRight, X, Check, ChevronLeft, AlertCircle, Star, Zap, Loader2,
  TrendingDown, MessageSquare, Camera
} from 'lucide-react';
import { useCexAccount } from '../context/CexAccountContext';
import { apiService } from '../services/apiService';
import { SUPPORTED_CEX, POPULAR_PAIRS } from '../constants';
import { API_BASE_URL } from '../config/api';

interface Execution {
  id: string;
  trade_group_id?: string;
  cex_account_id: number | null;
  trade_date: string;
  side: 'BUY' | 'SELL';
  price: string;
  qty: string;
  commission: string;
  fee: string;
}

interface AddManualTradeWizardProps {
  onClose: () => void;
  onSuccess: (newTrade?: any) => void;
}

const STEPS = ['Pair & Exchange', 'Executions', 'Setup & Analysis', 'Review & Submit'];

function newExec(): Execution {
  return {
    id: Math.random().toString(36).slice(2),
    cex_account_id: null,
    trade_date: new Date().toISOString().slice(0, 16),
    side: 'BUY',
    price: '',
    qty: '',
    commission: '',
    fee: '',
  };
}

const safeParseFloat = (val: string | number): number => {
  if (typeof val === 'number') return val;
  if (!val) return 0;
  const clean = val.toString().replace(/,/g, '').trim();
  return parseFloat(clean) || 0;
};

const AddManualTradeWizard: React.FC<AddManualTradeWizardProps> = ({ onClose, onSuccess }) => {
  const { accounts } = useCexAccount();
  const token = localStorage.getItem('auth_token') || '';

  const [step, setStep] = useState(0);
  // Auto-select the first account by default, or default to manual
  const [selectedAccountId, setSelectedAccountId] = useState<number | string | null>(() =>
    (accounts as any[]).length > 0 ? (accounts as any[])[0].id : 'manual'
  );
  const [pair, setPair] = useState('');
  const [pairQuery, setPairQuery] = useState('');
  const [pairSuggestions, setPairSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [executions, setExecutions] = useState<any[]>([]);
  const [unifiedTrade, setUnifiedTrade] = useState({
    cex_account_id: (accounts as any[]).length > 0 ? (accounts as any[])[0].id.toString() : 'manual',
    buy_date: new Date().toISOString().slice(0, 16),
    buy_price: '',
    qty: '',
    commission: '',
    fee: '',
    sell_date: new Date().toISOString().slice(0, 16),
    sell_price: ''
  });
  const [showSell, setShowSell] = useState(false);
  const [tradeType, setTradeType] = useState<'SPOT' | 'FUTURE'>('SPOT');
  const [futureDirection, setFutureDirection] = useState<'LONG' | 'SHORT'>('LONG');
  const [leverage, setLeverage] = useState('10');
  const [qtyMode, setQtyMode] = useState<'token' | 'usdt'>('token');
  const [usdtAmount, setUsdtAmount] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const sugRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<any>(null);

  // Manual Setup & Analysis Fields
  const [manualFields, setManualFields] = useState({
    session: '',
    market_cap: '',
    primary_setup_type: '',
    key_indicators: '',
    timeframe_analysis: '',
    risk_percentage: '',
    pre_trade_confidence: 5,
    emotional_load: 5,
    remarks: ''
  });
  const [showTimeframeDropdown, setShowTimeframeDropdown] = useState(false);

  const SETUP_CHOICES = ["Breakout", "Pullback", "Reversal", "Trend Continuation", "Range Trade", "News/Event"];
  const INDICATOR_CHOICES = ["EMA Crossover", "RSI Divergence", "Volume Spike", "Order Block", "Liquidity Grab"];
  const TIMEFRAME_CHOICES = ["15m", "1H", "4H", "Daily", "Weekly", "Monthly", "Yearly"];

  const handleToggleOption = (field: 'primary_setup_type' | 'key_indicators' | 'timeframe_analysis', option: string) => {
    const currentStr = manualFields[field] || '';
    const currentArr = currentStr ? currentStr.split(',').map((s: string) => s.trim()) : [];
    let newArr;
    if (currentArr.includes(option)) {
      newArr = currentArr.filter((s: string) => s !== option);
    } else {
      newArr = [...currentArr, option];
    }
    setManualFields({ ...manualFields, [field]: newArr.join(', ') });
  };

  const timeframeSelected = manualFields.timeframe_analysis
    ? manualFields.timeframe_analysis.split(',').map((s: string) => s.trim()).filter(Boolean)
    : [];

  const filteredPopularPairs = pairQuery.length >= 1
    ? POPULAR_PAIRS.filter(p =>
      p.symbol.toLowerCase().includes(pairQuery.toLowerCase()) ||
      p.label.toLowerCase().includes(pairQuery.toLowerCase())
    )
    : POPULAR_PAIRS;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (sugRef.current && !sugRef.current.contains(e.target as Node)) setShowSuggestions(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchSuggestions = (val: string) => {
    clearTimeout(debounceRef.current);
    if (val.length < 2) { setPairSuggestions([]); setShowSuggestions(false); return; }
    debounceRef.current = setTimeout(async () => {
      setLoadingSuggestions(true);
      try {
        const res = await apiService.getTradeSuggestions(val, token);
        if (res.success) { setPairSuggestions(res.data); setShowSuggestions(true); }
      } finally { setLoadingSuggestions(false); }
    }, 350);
  };

  const calcPnl = () => {
    let buyVal = 0, sellVal = 0, fees = 0;
    executions.forEach(e => {
      const p = safeParseFloat(e.price) || 0;
      const q = safeParseFloat(e.qty) || 0;
      const c = safeParseFloat(e.commission) || 0;
      const f = safeParseFloat(e.fee) || 0;
      if (e.side === 'BUY') buyVal += p * q;
      else sellVal += p * q;
      fees += c + f;
    });
    const isFuture = tradeType === 'FUTURE';
    const isShort = isFuture && futureDirection === 'SHORT';
    const gross = sellVal - buyVal;
    const net = gross - fees;
    const hasSell = showSell;
    const status = !hasSell ? 'OPEN' : net > 0 ? 'WIN' : net < 0 ? 'LOSE' : 'WIN';
    const entryVal = isShort ? sellVal : buyVal;
    return { gross, net, fees, buyVal: entryVal, status };
  };

  const validateStep0 = () => pair.trim().length >= 2;
  const validateStep1 = () => {
    const u = unifiedTrade;
    const buyPrice = safeParseFloat(u.buy_price);
    const qty = safeParseFloat(u.qty);
    if (!u.buy_date || !u.buy_price || !(buyPrice > 0) || !u.qty || !(qty > 0)) return false;
    if (showSell && (!u.sell_date || !u.sell_price || !(safeParseFloat(u.sell_price) > 0))) return false;
    return true;
  };

  const handleNext = () => {
    if (step === 0 && validateStep0()) {
      // Resolve account: use selected, else first account, else empty
      const resolvedId = (selectedAccountId ?? (accounts as any[])[0]?.id ?? 'manual').toString();
      setUnifiedTrade(prev => ({ ...prev, cex_account_id: resolvedId }));
      setStep(1);
    } else if (step === 1 && validateStep1()) {
      const execs = [];
      const isFuture = tradeType === 'FUTURE';
      const side1 = (isFuture && futureDirection === 'SHORT' ? 'SELL' : 'BUY') as 'BUY' | 'SELL';
      const side2 = (isFuture && futureDirection === 'SHORT' ? 'BUY' : 'SELL') as 'BUY' | 'SELL';
      
      const resolvedAccId = unifiedTrade.cex_account_id.startsWith('virtual:')
        ? unifiedTrade.cex_account_id
        : (parseInt(unifiedTrade.cex_account_id) || 0);

      execs.push({
        id: 'buy-1',
        cex_account_id: resolvedAccId,
        side: side1,
        trade_date: unifiedTrade.buy_date,
        price: unifiedTrade.buy_price,
        qty: unifiedTrade.qty,
        commission: unifiedTrade.commission || '0',
        fee: unifiedTrade.fee || '0'
      });
      if (showSell) {
        execs.push({
          id: 'sell-1',
          cex_account_id: resolvedAccId,
          side: side2,
          trade_date: unifiedTrade.sell_date,
          price: unifiedTrade.sell_price,
          qty: unifiedTrade.qty,
          commission: '0',
          fee: '0'
        });
      }
      setExecutions(execs);
      setStep(2);
    } else if (step === 2) {
      setStep(3);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true); setError('');
    try {
      let resolvedCexAccountId = 0;

      // Handle virtual CEX account creation if selected
      const firstExecCexId = executions[0]?.cex_account_id;
      if (typeof firstExecCexId === 'string' && firstExecCexId.startsWith('virtual:')) {
        const cexName = firstExecCexId.replace('virtual:', '');
        if (cexName !== 'manual') {
          const newAccountRes = await fetch(`${API_BASE_URL}/api/cex-accounts`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              cex_name: cexName,
              account_label: 'Virtual Wallet',
            }),
          });
          const newAccountData = await newAccountRes.json();
          if (newAccountRes.ok && newAccountData.success) {
            resolvedCexAccountId = newAccountData.data.id;
          } else {
            throw new Error(newAccountData.message || 'Failed to create virtual CEX account');
          }
        }
      } else if (firstExecCexId && firstExecCexId !== 'manual') {
        resolvedCexAccountId = typeof firstExecCexId === 'number' ? firstExecCexId : parseInt(firstExecCexId);
      }

      const payload = {
        pair: pair.toUpperCase().trim(),
        executions: executions.map(e => ({
          cex_account_id: resolvedCexAccountId,
          trade_date: e.trade_date,
          side: e.side,
          price: safeParseFloat(e.price),
          qty: safeParseFloat(e.qty),
          commission: safeParseFloat(e.commission) || 0,
          fee: safeParseFloat(e.fee) || 0,
          session: manualFields.session.trim() || null,
          market_cap: manualFields.market_cap.trim() || null,
          primary_setup_type: manualFields.primary_setup_type.trim() || null,
          key_indicators: manualFields.key_indicators.trim() || null,
          timeframe_analysis: manualFields.timeframe_analysis.trim() || null,
          risk_percentage: manualFields.risk_percentage ? (safeParseFloat(manualFields.risk_percentage) || null) : null,
          pre_trade_confidence: manualFields.pre_trade_confidence || 5,
          emotional_load: manualFields.emotional_load || 5,
          remarks: manualFields.remarks.trim() || null,
          leverage: tradeType === 'FUTURE' ? safeParseFloat(leverage) : null,
          direction: tradeType === 'FUTURE' ? futureDirection.toUpperCase() : e.side,
        })),
      };
      const res = await apiService.storeManualTrade(payload, token);
      onSuccess(res?.data);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save trade');
    } finally { setIsSubmitting(false); }
  };

  const pnl = calcPnl();

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose} className="fixed inset-0 bg-black/70 backdrop-blur-md z-50" />
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }} transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-[#0D0D0D] border border-neutral-800 rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">

          {/* Header */}
          <div className="px-8 pt-8 pb-6 border-b border-neutral-800/60 bg-gradient-to-r from-[#0D0D0D] to-[#111]">
            <div className="flex justify-between items-start mb-6">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-[10px] font-black uppercase tracking-widest text-red-500">Manual Entry</span>
                </div>
                <h2 className="text-2xl font-black text-white tracking-tight">+ Trade Baru</h2>
                <p className="text-xs text-neutral-500 mt-1">Multi-exchange execution grouping</p>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-neutral-800 rounded-xl transition-colors">
                <X size={18} className="text-neutral-400" />
              </button>
            </div>
            {/* Step indicator */}
            <div className="flex items-center gap-2">
              {STEPS.map((s, i) => (
                <React.Fragment key={i}>
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider transition-all ${i === step ? 'bg-red-600/20 text-red-400 border border-red-600/40' : i < step ? 'bg-green-600/10 text-green-500' : 'text-neutral-600'}`}>
                    {i < step ? <Check size={10} /> : <span className="w-3 text-center">{i + 1}</span>}
                    {s}
                  </div>
                  {i < STEPS.length - 1 && <ChevronRight size={12} className="text-neutral-700 flex-shrink-0" />}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-8" data-lenis-prevent>
            <AnimatePresence mode="wait">

              {/* STEP 0: Exchange + Pair */}
              {step === 0 && (
                <motion.div key="step0" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-7">

                  {/* ── Exchange Picker ── */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <label className="text-xs font-black uppercase tracking-widest text-neutral-400">Exchange</label>
                      {selectedAccountId === 'manual' && (
                        <span className="text-[9px] font-bold text-neutral-600 bg-neutral-800 px-2 py-0.5 rounded-full">Optional</span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      {/* Connected Accounts */}
                      {(accounts as any[]).map((acct) => {
                        const cex = SUPPORTED_CEX.find(c => c.value.toLowerCase() === (acct.cex_name || '').toLowerCase());
                        const isSelected = selectedAccountId === acct.id;
                        return (
                          <button
                            key={`acct-${acct.id}`}
                            onClick={() => setSelectedAccountId(isSelected ? 'manual' : acct.id)}
                            style={isSelected && cex?.color ? { borderColor: cex.color + '60', boxShadow: `0 0 0 1px ${cex.color}25` } : {}}
                            className={`relative flex items-center gap-3 px-4 py-3 rounded-2xl border transition-all text-left ${isSelected
                              ? 'bg-neutral-800/80 border-neutral-600'
                              : 'bg-neutral-900/50 border-neutral-800 hover:border-neutral-700 hover:bg-neutral-900'
                              }`}>
                            {cex?.icon ? (
                              <img src={cex.icon} alt={cex.label} className="w-8 h-8 object-contain rounded-xl flex-shrink-0" />
                            ) : (
                              <div className="w-8 h-8 bg-neutral-700 rounded-xl" title="No icon" />
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-black text-white truncate">{acct.cex_display_name}</p>
                              <p className="text-[10px] text-neutral-500 truncate">{acct.account_label || cex?.label}</p>
                            </div>
                            {isSelected && (
                              <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                                <Check size={9} className="text-black" />
                              </div>
                            )}
                          </button>
                        );
                      })}

                      {/* Supported CEXes that are NOT connected */}
                      {SUPPORTED_CEX.map((cex) => {
                        if (cex.value === 'manual') return null;

                        // Check if user already has this CEX connected
                        const hasConnection = (accounts as any[]).some(a => a.cex_name.toLowerCase() === cex.value.toLowerCase());
                        if (hasConnection) return null;

                        const isSelected = selectedAccountId === `virtual:${cex.value}`;

                        return (
                          <button
                            key={`virtual-${cex.value}`}
                            onClick={() => setSelectedAccountId(isSelected ? 'manual' : `virtual:${cex.value}`)}
                            style={isSelected && cex.color ? { borderColor: cex.color + '60', boxShadow: `0 0 0 1px ${cex.color}25` } : {}}
                            className={`relative flex items-center gap-3 px-4 py-3 rounded-2xl border transition-all text-left ${isSelected
                              ? 'bg-neutral-800/80 border-neutral-600'
                              : 'bg-neutral-900/50 border-neutral-800 hover:border-neutral-700 hover:bg-neutral-900'
                              }`}>
                            {cex.icon ? (
                              <img src={cex.icon} alt={cex.label} className="w-8 h-8 object-contain rounded-xl flex-shrink-0" />
                            ) : (
                              <div className="w-8 h-8 bg-neutral-700 rounded-xl" title="No icon" />
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-black text-white truncate">{cex.label}</p>
                              <p className="text-[10px] text-neutral-500 truncate">Virtual Wallet (No API)</p>
                            </div>
                            {isSelected && (
                              <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                                <Check size={9} className="text-black" />
                              </div>
                            )}
                          </button>
                        );
                      })}

                      {/* Manual / Virtual Journal Option */}
                      {(() => {
                        const cex = SUPPORTED_CEX.find(c => c.value === 'manual')!;
                        const isSelected = selectedAccountId === 'manual';
                        return (
                          <button
                            key="virtual-manual"
                            onClick={() => setSelectedAccountId('manual')}
                            style={isSelected && cex.color ? { borderColor: cex.color + '60', boxShadow: `0 0 0 1px ${cex.color}25` } : {}}
                            className={`relative flex items-center gap-3 px-4 py-3 rounded-2xl border transition-all text-left ${isSelected
                              ? 'bg-neutral-800/80 border-neutral-600'
                              : 'bg-neutral-900/50 border-neutral-800 hover:border-neutral-700 hover:bg-neutral-900'
                              }`}>
                            {cex.icon ? (
                              <img src={cex.icon} alt={cex.label} className="w-8 h-8 object-contain rounded-xl flex-shrink-0" />
                            ) : (
                              <div className="w-8 h-8 bg-neutral-700 rounded-xl" title="No icon" />
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-black text-white truncate">Manual / Other</p>
                              <p className="text-[10px] text-neutral-500 truncate">No specific exchange</p>
                            </div>
                            {isSelected && (
                              <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                                <Check size={9} className="text-black" />
                              </div>
                            )}
                          </button>
                        );
                      })()}
                    </div>

                    {selectedAccountId && selectedAccountId !== 'manual' && (
                      <p className="text-[10px] text-neutral-600 mt-2 flex items-center gap-1">
                        <Check size={9} className="text-green-500" />
                        {typeof selectedAccountId === 'string' && selectedAccountId.startsWith('virtual:') 
                          ? "Virtual account akan otomatis dibuat di sistem untuk melacak transaksi manual ini"
                          : "Exchange ini akan jadi default untuk semua execution baru"}
                      </p>
                    )}
                  </div>

                  {/* ── Pair Search ── */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <label className="text-xs font-black uppercase tracking-widest text-neutral-400">Trading Pair</label>
                      <Star size={10} className="text-red-500" />
                    </div>
                    <div className="relative" ref={sugRef}>
                      <div className="relative">
                        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500" />
                        <input
                          autoFocus
                          type="text"
                          value={pair || pairQuery}
                          onChange={e => {
                            const val = e.target.value.toUpperCase();
                            setPair(val);
                            setPairQuery(val);
                            fetchSuggestions(val);
                          }}
                          onFocus={() => { if (pairSuggestions.length > 0) setShowSuggestions(true); }}
                          placeholder="Cari pair... BTCUSDT"
                          className="w-full bg-neutral-900 border border-neutral-700 rounded-2xl py-4 pl-11 pr-10 text-white text-base font-bold focus:outline-none focus:border-red-500/60 transition-all placeholder-neutral-600 tracking-wide"
                        />
                        {loadingSuggestions && <Loader2 size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-500 animate-spin" />}
                        {pair.length > 0 && !loadingSuggestions && (
                          <button onClick={() => { setPair(''); setPairQuery(''); setShowSuggestions(false); }}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-600 hover:text-neutral-400 transition-colors">
                            <X size={14} />
                          </button>
                        )}
                      </div>

                      <AnimatePresence>
                        {showSuggestions && pairSuggestions.length > 0 && (
                          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                            className="absolute top-full left-0 right-0 mt-2 bg-[#161616] border border-neutral-800 rounded-2xl overflow-hidden z-10 shadow-xl">
                            <p className="px-4 pt-3 pb-1 text-[9px] font-black uppercase tracking-widest text-neutral-600">Dari trade history kamu</p>
                            {pairSuggestions.map((s: any) => (
                              <button key={s.id} onClick={() => { setPair(s.pairs); setPairQuery(s.pairs); setShowSuggestions(false); }}
                                className="w-full flex items-center justify-between px-4 py-3 hover:bg-neutral-800/60 transition-colors text-left border-t border-neutral-800/30">
                                <div>
                                  <p className="text-sm font-bold text-white">{s.pairs}</p>
                                  <p className="text-[10px] text-neutral-500">{s.label}</p>
                                </div>
                                <span className="text-[9px] font-black uppercase px-2 py-0.5 rounded bg-neutral-800 text-neutral-400">{s.exchange}</span>
                              </button>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {!showSuggestions && (
                      <div className="mt-3">
                        <p className="text-[9px] font-black uppercase tracking-widest text-neutral-600 mb-2">Popular Pairs</p>
                        <div className="grid grid-cols-4 gap-1.5">
                          {filteredPopularPairs.slice(0, 20).map(p => (
                            <button key={p.symbol}
                              onClick={() => { setPair(p.symbol); setPairQuery(p.symbol); }}
                              className={`flex flex-col items-start px-3 py-2 rounded-xl border transition-all ${pair === p.symbol
                                ? 'bg-red-600/20 border-red-600/50 text-red-400'
                                : 'bg-neutral-900/60 border-neutral-800 hover:border-neutral-700 hover:bg-neutral-800/60 text-neutral-300'
                                }`}>
                              <span className="text-[11px] font-black tracking-tight leading-tight">{p.base}</span>
                              <span className="text-[9px] text-neutral-600 leading-tight">/{p.quote}</span>
                            </button>
                          ))}
                        </div>
                        {filteredPopularPairs.length === 0 && (
                          <p className="text-[10px] text-neutral-600 text-center py-3">Tidak ada hasil. Ketik manual di atas.</p>
                        )}
                      </div>
                    )}

                    {pair.length >= 2 && (
                      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                        className="mt-3 flex items-center gap-3 bg-neutral-900/50 border border-neutral-800 rounded-xl px-4 py-3">
                        <Zap size={14} className="text-red-500 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-black text-white tracking-tight">{pair.toUpperCase()}</p>
                          <p className="text-[10px] text-neutral-500">1 trade group · siap dilanjutkan</p>
                        </div>
                        <Check size={14} className="text-green-500 ml-auto" />
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              )}

              {/* STEP 1: Unified Form */}
              {step === 1 && (
                <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-lg font-black text-white">{pair.toUpperCase()}</p>
                      <p className="text-[10px] text-neutral-500">Log your trade details</p>
                    </div>
                  </div>

                  <div className="bg-neutral-900/40 border border-neutral-800 rounded-2xl p-5 space-y-5">
                    {/* Account badge */}
                    {(() => {
                      const isVirtual = typeof unifiedTrade.cex_account_id === 'string' && unifiedTrade.cex_account_id.startsWith('virtual:');
                      if (isVirtual) {
                        const cexName = (unifiedTrade.cex_account_id as string).replace('virtual:', '');
                        const cex = SUPPORTED_CEX.find(c => c.value.toLowerCase() === cexName.toLowerCase());
                        return (
                          <div className="flex items-center gap-2 px-3 py-2 bg-neutral-800/60 border border-neutral-700 rounded-xl w-fit">
                            {cex?.icon ? (
                              <img src={cex.icon} alt={cex.label} className="w-5 h-5 object-contain rounded" />
                            ) : (
                              <div className="w-5 h-5 bg-neutral-700 rounded" title="No icon" />
                            )}
                            <p className="text-xs font-bold text-white">{cex?.label} (Virtual)</p>
                            <span className="text-[9px] text-neutral-500 bg-neutral-900 px-1.5 py-0.5 rounded font-bold uppercase tracking-wide">Selected</span>
                          </div>
                        );
                      }
                      const acct = (accounts as any[]).find(a => a.id.toString() === unifiedTrade.cex_account_id);
                      const cex = SUPPORTED_CEX.find(c => c.value.toLowerCase() === (acct?.cex_name || '').toLowerCase());
                      return acct ? (
                        <div className="flex items-center gap-2 px-3 py-2 bg-neutral-800/60 border border-neutral-700 rounded-xl w-fit">
                          {cex?.icon ? (
                            <img src={cex.icon} alt={cex.label} className="w-5 h-5 object-contain rounded" />
                          ) : (
                            <div className="w-5 h-5 bg-neutral-700 rounded" title="No icon" />
                          )}
                          <p className="text-xs font-bold text-white">{acct.cex_display_name}</p>
                          <span className="text-[9px] text-neutral-500 bg-neutral-900 px-1.5 py-0.5 rounded font-bold uppercase tracking-wide">Selected</span>
                        </div>
                      ) : (
                        <p className="text-[10px] text-neutral-600">No exchange selected — will use default</p>
                      );
                    })()}

                    {/* Category Selection: SPOT & FUTURE */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-2">Trade Category</label>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setTradeType('SPOT')}
                            className={`flex-1 py-2.5 rounded-xl border text-xs font-bold text-center transition-all ${tradeType === 'SPOT'
                              ? 'bg-red-600/20 border-red-600/60 text-red-400 font-black'
                              : 'bg-neutral-800/50 border-neutral-700 text-neutral-400 hover:border-neutral-600'
                              }`}
                          >
                            SPOT
                          </button>
                          <button
                            type="button"
                            onClick={() => setTradeType('FUTURE')}
                            className={`flex-1 py-2.5 rounded-xl border text-xs font-bold text-center transition-all ${tradeType === 'FUTURE'
                              ? 'bg-red-600/20 border-red-600/60 text-red-400 font-black'
                              : 'bg-neutral-800/50 border-neutral-700 text-neutral-400 hover:border-neutral-600'
                              }`}
                          >
                            FUTURE
                          </button>
                        </div>
                      </div>

                      {tradeType === 'FUTURE' && (
                        <div>
                          <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-2">Position Side</label>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => setFutureDirection('LONG')}
                              className={`flex-1 py-2.5 rounded-xl border text-xs font-bold text-center transition-all ${futureDirection === 'LONG'
                                ? 'bg-blue-600/20 border-blue-600/60 text-blue-400 font-black'
                                : 'bg-neutral-800/50 border-neutral-700 text-neutral-400 hover:border-neutral-600'
                                }`}
                            >
                              LONG
                            </button>
                            <button
                              type="button"
                              onClick={() => setFutureDirection('SHORT')}
                              className={`flex-1 py-2.5 rounded-xl border text-xs font-bold text-center transition-all ${futureDirection === 'SHORT'
                                ? 'bg-amber-600/20 border-amber-600/60 text-amber-400 font-black'
                                : 'bg-neutral-800/50 border-neutral-700 text-neutral-400 hover:border-neutral-600'
                                }`}
                            >
                              SHORT
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Leverage Size (If FUTURE is selected) */}
                    {tradeType === 'FUTURE' && (
                      <div className="p-4 bg-neutral-800/30 border border-neutral-800 rounded-xl space-y-3">
                        <div className="flex items-center justify-between">
                          <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500">Leverage Size</label>
                          <span className="text-xs font-black text-red-400 font-mono">{leverage}X</span>
                        </div>
                        <div className="flex gap-3 items-center">
                          <input
                            type="range"
                            min="1"
                            max="125"
                            value={leverage}
                            onChange={e => setLeverage(e.target.value)}
                            className="flex-1 h-1 bg-neutral-800 rounded-lg appearance-none cursor-pointer accent-red-600"
                          />
                          <input
                            type="number"
                            min="1"
                            max="125"
                            value={leverage}
                            onChange={e => {
                              const val = Math.max(1, Math.min(125, parseInt(e.target.value) || 1));
                              setLeverage(val.toString());
                            }}
                            className="w-16 bg-neutral-800 border border-neutral-700 rounded-lg px-2 py-1 text-xs text-white text-center font-bold font-mono focus:border-red-500 outline-none"
                          />
                        </div>
                        <div className="flex gap-1.5 justify-between">
                          {['2x', '5x', '10x', '20x', '50x', '100x'].map(lev => {
                            const val = lev.replace('x', '');
                            return (
                              <button
                                key={lev}
                                type="button"
                                onClick={() => setLeverage(val)}
                                className={`px-2.5 py-1 rounded bg-neutral-900 border text-[10px] font-bold font-mono transition-all ${leverage === val ? 'border-red-500/50 text-red-400' : 'border-neutral-800 text-neutral-500 hover:text-white'
                                  }`}
                              >
                                {lev}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      {/* BUY SECTION */}
                      <div className="col-span-2 md:col-span-1 p-4 bg-green-900/10 border border-green-600/20 rounded-xl space-y-4">
                        <p className={`text-xs font-black uppercase tracking-widest flex items-center gap-2 ${tradeType === 'FUTURE' && futureDirection === 'SHORT' ? 'text-red-500' : 'text-green-500'}`}>
                          <Zap size={14} /> {
                            tradeType === 'FUTURE'
                              ? (futureDirection === 'SHORT' ? 'Short Entry (Sell)' : 'Long Entry (Buy)')
                              : 'Buy Details (Entry)'
                          }
                        </p>
                        <div>
                          <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-1.5">Entry Date & Time</label>
                          <input type="datetime-local" value={unifiedTrade.buy_date} onChange={e => setUnifiedTrade({ ...unifiedTrade, buy_date: e.target.value })}
                            className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-sm text-white focus:border-green-500/50 outline-none" />
                        </div>
                        <div>
                          <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-1.5">Entry Price (USDT)</label>
                          <input type="number" step="any" min="0" value={unifiedTrade.buy_price} onChange={e => setUnifiedTrade({ ...unifiedTrade, buy_price: e.target.value })} placeholder="e.g. 70000"
                            className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-sm text-white font-mono focus:border-green-500/50 outline-none" />
                        </div>
                      </div>

                      {/* SELL SECTION */}
                      <div className="col-span-2 md:col-span-1 p-4 bg-red-900/10 border border-red-600/20 rounded-xl space-y-4 relative">
                        <div className="flex items-center justify-between">
                          <p className={`text-xs font-black uppercase tracking-widest flex items-center gap-2 ${tradeType === 'FUTURE' && futureDirection === 'SHORT' ? 'text-green-500' : 'text-red-500'}`}>
                            <TrendingDown size={14} /> {
                              tradeType === 'FUTURE'
                                ? (futureDirection === 'SHORT' ? 'Short Exit (Buy)' : 'Long Exit (Sell)')
                                : 'Sell Details (Exit)'
                            }
                          </p>
                          <button onClick={() => setShowSell(!showSell)} className="text-[10px] font-bold text-red-400 hover:text-red-300">
                            {showSell ? 'Clear & Hide' : 'Add Exit'}
                          </button>
                        </div>

                        {!showSell ? (
                          <div className={`absolute inset-0 flex items-center justify-center bg-neutral-900/40 backdrop-blur-sm rounded-xl z-10 border border-dashed ${tradeType === 'FUTURE' && futureDirection === 'SHORT' ? 'border-green-500/30' : 'border-red-500/30'}`}>
                            <button onClick={() => setShowSell(true)} className={`px-4 py-2 text-xs font-bold rounded-lg transition-all ${tradeType === 'FUTURE' && futureDirection === 'SHORT' ? 'bg-green-600/20 text-green-400 hover:bg-green-600/30' : 'bg-red-600/20 text-red-400 hover:bg-red-600/30'}`}>
                              + Add Exit Execution
                            </button>
                          </div>
                        ) : null}

                        <div>
                          <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-1.5">Exit Date & Time</label>
                          <input type="datetime-local" value={unifiedTrade.sell_date} onChange={e => setUnifiedTrade({ ...unifiedTrade, sell_date: e.target.value })}
                            className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-sm text-white focus:border-red-500/50 outline-none" />
                        </div>
                        <div>
                          <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-1.5">Exit Price (USDT)</label>
                          <input type="number" step="any" min="0" value={unifiedTrade.sell_price} onChange={e => setUnifiedTrade({ ...unifiedTrade, sell_price: e.target.value })} placeholder="e.g. 75000"
                            className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-sm text-white font-mono focus:border-red-500/50 outline-none" />
                        </div>
                      </div>

                      {/* COMMON DETAILS */}
                      <div className="col-span-2 p-4 bg-neutral-800/30 border border-neutral-800 rounded-xl space-y-4">
                        <div className="grid grid-cols-3 gap-3">
                          <div className="col-span-3">
                            {/* Qty Mode Toggle */}
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-[10px] font-black uppercase tracking-widest text-neutral-500">
                                {qtyMode === 'token' ? 'Token Quantity' : 'Total USDT'}
                              </label>
                              <div className="flex bg-neutral-900 border border-neutral-700 rounded-lg p-0.5 gap-0.5">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setQtyMode('token');
                                    setUsdtAmount('');
                                  }}
                                  className={`px-2.5 py-1 rounded text-[9px] font-black uppercase tracking-wider transition-all ${
                                    qtyMode === 'token'
                                      ? 'bg-neutral-700 text-white'
                                      : 'text-neutral-500 hover:text-neutral-300'
                                  }`}
                                >
                                  By Token
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setQtyMode('usdt');
                                    setUnifiedTrade(prev => ({ ...prev, qty: '' }));
                                  }}
                                  className={`px-2.5 py-1 rounded text-[9px] font-black uppercase tracking-wider transition-all ${
                                    qtyMode === 'usdt'
                                      ? 'bg-neutral-700 text-white'
                                      : 'text-neutral-500 hover:text-neutral-300'
                                  }`}
                                >
                                  By USDT
                                </button>
                              </div>
                            </div>

                            {qtyMode === 'token' ? (
                              <div className="relative">
                                <input
                                  type="number" step="any" min="0"
                                  value={unifiedTrade.qty}
                                  onChange={e => setUnifiedTrade({ ...unifiedTrade, qty: e.target.value })}
                                  placeholder="e.g. 0.01"
                                  className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2.5 pr-16 text-sm text-white font-mono focus:border-neutral-500 outline-none"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-black text-neutral-500 uppercase">
                                  {pair.split('USDT')[0] || pair.split('/')[0] || 'TOKEN'}
                                </span>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                <div className="relative">
                                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-black text-neutral-400">$</span>
                                  <input
                                    type="number" step="any" min="0"
                                    value={usdtAmount}
                                    onChange={e => {
                                      const usdt = e.target.value;
                                      setUsdtAmount(usdt);
                                      const price = safeParseFloat(unifiedTrade.buy_price);
                                      if (usdt && price > 0) {
                                        const tokenQty = (safeParseFloat(usdt) / price);
                                        setUnifiedTrade(prev => ({ ...prev, qty: tokenQty.toFixed(8).replace(/\.?0+$/, '') }));
                                      } else {
                                        setUnifiedTrade(prev => ({ ...prev, qty: '' }));
                                      }
                                    }}
                                    placeholder="e.g. 1000"
                                    className="w-full bg-neutral-800 border border-neutral-700 rounded-lg pl-7 pr-16 py-2.5 text-sm text-white font-mono focus:border-neutral-500 outline-none"
                                  />
                                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-black text-neutral-500">USDT</span>
                                </div>
                                {/* Live token qty preview */}
                                {usdtAmount && safeParseFloat(unifiedTrade.buy_price) > 0 && (
                                  <div className="flex items-center gap-2 px-3 py-2 bg-neutral-900/60 border border-neutral-700/50 rounded-lg">
                                    <span className="text-[10px] text-neutral-500">≈</span>
                                    <span className="text-sm font-black font-mono text-white">
                                      {(safeParseFloat(usdtAmount) / safeParseFloat(unifiedTrade.buy_price)).toFixed(8).replace(/\.?0+$/, '')}
                                    </span>
                                    <span className="text-[10px] font-black text-neutral-500 uppercase">
                                      {pair.split('USDT')[0] || pair.split('/')[0] || 'TOKEN'}
                                    </span>
                                    <span className="ml-auto text-[9px] text-neutral-600">calculated qty</span>
                                  </div>
                                )}
                                {usdtAmount && !safeParseFloat(unifiedTrade.buy_price) && (
                                  <p className="text-[10px] text-amber-500 font-bold">⚠ Isi Entry Price dulu untuk kalkulasi otomatis</p>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="col-span-3 sm:col-span-1">
                            <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-1.5">Total Commission</label>
                            <input type="number" step="any" min="0" value={unifiedTrade.commission} onChange={e => setUnifiedTrade({ ...unifiedTrade, commission: e.target.value })} placeholder="0.00"
                              className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-sm text-white font-mono focus:border-neutral-500 outline-none" />
                          </div>
                          <div className="col-span-3 sm:col-span-1">
                            <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-1.5">Total Fee</label>
                            <input type="number" step="any" min="0" value={unifiedTrade.fee} onChange={e => setUnifiedTrade({ ...unifiedTrade, fee: e.target.value })} placeholder="0.00"
                              className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-sm text-white font-mono focus:border-neutral-500 outline-none" />
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                </motion.div>
              )}

              {/* STEP 2: Setup & Analysis */}
              {step === 2 && (
                <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
                  <div>
                    <p className="text-lg font-black text-white">Setup & Analysis</p>
                    <p className="text-xs text-neutral-500 mt-1">Add manual trading journal metrics (optional)</p>
                  </div>

                  <div className="bg-neutral-900/40 border border-neutral-800 rounded-2xl p-6 space-y-6 max-h-[50vh] overflow-y-auto pr-1" data-lenis-prevent>
                    {/* Session Selector */}
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-2">Trading Session</label>
                      <div className="grid grid-cols-3 gap-2">
                        {["Asian Session (07:00 - 16:00 WIB)", "London Session (16:00 - 23:00 WIB)", "US Session (23:00 - 07:00 WIB)"].map(sess => (
                          <button
                            key={sess}
                            type="button"
                            onClick={() => setManualFields({ ...manualFields, session: sess })}
                            className={`px-3 py-2.5 rounded-xl border text-xs font-bold text-center transition-all ${manualFields.session === sess
                              ? 'bg-red-600/20 border-red-600/60 text-red-400'
                              : 'bg-neutral-800/50 border-neutral-700 text-neutral-400 hover:border-neutral-600'
                              }`}
                          >
                            {sess}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Market Cap */}
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-2">Asset Market Cap Class</label>
                      <div className="grid grid-cols-3 gap-2">
                        {["High Cap", "Mid Cap", "Low Cap"].map(cap => (
                          <button
                            key={cap}
                            type="button"
                            onClick={() => setManualFields({ ...manualFields, market_cap: cap })}
                            className={`px-3 py-2.5 rounded-xl border text-xs font-bold text-center transition-all ${manualFields.market_cap === cap
                              ? 'bg-red-600/20 border-red-600/60 text-red-400'
                              : 'bg-neutral-800/50 border-neutral-700 text-neutral-400 hover:border-neutral-600'
                              }`}
                          >
                            {cap}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Primary Setup Type (Multiselect tags) */}
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-2">Primary Setup Type (Select multiple)</label>
                      <div className="flex gap-2 flex-wrap">
                        {SETUP_CHOICES.map(setup => {
                          const isSelected = manualFields.primary_setup_type.split(',').map(s => s.trim()).includes(setup);
                          return (
                            <button
                              key={setup}
                              type="button"
                              onClick={() => handleToggleOption('primary_setup_type', setup)}
                              className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all ${isSelected
                                ? 'bg-[#E50914] border-[#E50914] text-white'
                                : 'bg-neutral-800/50 border-neutral-700 text-neutral-400'
                                }`}
                            >
                              {setup}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Key Indicators (Multiselect tags) */}
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-2">Key Indicators (Select multiple)</label>
                      <div className="flex gap-2 flex-wrap">
                        {INDICATOR_CHOICES.map(ind => {
                          const isSelected = manualFields.key_indicators.split(',').map(s => s.trim()).includes(ind);
                          return (
                            <button
                              key={ind}
                              type="button"
                              onClick={() => handleToggleOption('key_indicators', ind)}
                              className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all ${isSelected
                                ? 'bg-[#E50914] border-[#E50914] text-white'
                                : 'bg-neutral-800/50 border-neutral-700 text-neutral-400'
                                }`}
                            >
                              {ind}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Timeframe Analysis (Custom Multiselect Dropdown) */}
                    <div className="relative">
                      <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-2">Timeframe Analysis (Select multiple)</label>
                      <button
                        type="button"
                        onClick={() => setShowTimeframeDropdown(!showTimeframeDropdown)}
                        className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2.5 text-xs text-left text-neutral-300 flex justify-between items-center outline-none"
                      >
                        <span className="truncate">
                          {timeframeSelected.length > 0 ? timeframeSelected.join(', ') : 'Select Timeframes'}
                        </span>
                        <ChevronRight size={14} className={`transform transition-transform ${showTimeframeDropdown ? 'rotate-90' : ''}`} />
                      </button>

                      {showTimeframeDropdown && (
                        <div className="absolute top-full left-0 right-0 mt-1.5 bg-[#161616] border border-neutral-800 rounded-xl overflow-hidden z-30 shadow-2xl p-1.5 max-h-[180px] overflow-y-auto" data-lenis-prevent>
                          {TIMEFRAME_CHOICES.map(tf => {
                            const isSelected = timeframeSelected.includes(tf);
                            return (
                              <button
                                key={tf}
                                type="button"
                                onClick={() => handleToggleOption('timeframe_analysis', tf)}
                                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-semibold ${isSelected ? 'bg-neutral-800 text-white' : 'text-neutral-400 hover:bg-neutral-900'
                                  }`}
                              >
                                <span>{tf} Timeframe</span>
                                {isSelected && <Check size={12} className="text-red-500" />}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Risk Percentage */}
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-2">Risk Percentage (%)</label>
                      <input
                        type="number"
                        step="any"
                        min="0"
                        value={manualFields.risk_percentage}
                        onChange={e => setManualFields({ ...manualFields, risk_percentage: e.target.value })}
                        placeholder="e.g. 1.5"
                        className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2 text-sm text-white font-mono focus:border-neutral-500 outline-none"
                      />
                    </div>

                    {/* Confidence & Emotional Load Sliders */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-2">
                          Pre-trade Confidence ({manualFields.pre_trade_confidence}/10)
                        </label>
                        <input
                          type="range"
                          min="1"
                          max="10"
                          value={manualFields.pre_trade_confidence}
                          onChange={e => setManualFields({ ...manualFields, pre_trade_confidence: parseInt(e.target.value) })}
                          className="w-full h-1 bg-neutral-800 rounded-lg appearance-none cursor-pointer accent-red-600"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-2">
                          Emotional Load ({manualFields.emotional_load}/10)
                        </label>
                        <input
                          type="range"
                          min="1"
                          max="10"
                          value={manualFields.emotional_load}
                          onChange={e => setManualFields({ ...manualFields, emotional_load: parseInt(e.target.value) })}
                          className="w-full h-1 bg-neutral-800 rounded-lg appearance-none cursor-pointer accent-red-600"
                        />
                      </div>
                    </div>

                    {/* Remarks */}
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-2">Remarks & Journal Entry</label>
                      <textarea
                        value={manualFields.remarks}
                        onChange={e => setManualFields({ ...manualFields, remarks: e.target.value })}
                        placeholder="Mental state, setup trigger notes..."
                        className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2.5 text-xs text-neutral-300 resize-none h-20 outline-none focus:border-neutral-600"
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              {/* STEP 3: Review */}
              {step === 3 && (
                <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
                  <div className="bg-neutral-900/40 border border-neutral-800 rounded-2xl p-6">
                    <p className="text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-4">Summary</p>
                    <div className="flex items-end justify-between mb-6">
                      <div>
                        <p className="text-4xl font-black text-white tracking-tight">{pair.toUpperCase()}</p>
                        <p className="text-xs text-neutral-500 mt-1">
                          {executions.length} execution{executions.length !== 1 ? 's' : ''} · {executions.filter(e => e.side === 'BUY').length} BUY / {executions.filter(e => e.side === 'SELL').length} SELL
                        </p>
                      </div>
                      <div className={`px-3 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider ${pnl.status === 'WIN' ? 'bg-green-600/20 text-green-400 border border-green-600/40' : pnl.status === 'LOSE' ? 'bg-red-600/20 text-red-400 border border-red-600/40' : 'bg-neutral-800 text-neutral-400 border border-neutral-700'}`}>
                        {pnl.status}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-neutral-800/50 rounded-xl p-4">
                        <p className="text-[9px] font-black uppercase tracking-widest text-neutral-500 mb-1">Net P&L</p>
                        <p className={`text-lg font-black font-mono ${pnl.status === 'WIN' ? 'text-green-400' : pnl.status === 'LOSE' ? 'text-red-400' : 'text-neutral-400'}`}>
                          {pnl.net >= 0 ? '+' : ''}{pnl.net.toFixed(4)}
                        </p>
                      </div>
                      <div className="bg-neutral-800/50 rounded-xl p-4">
                        <p className="text-[9px] font-black uppercase tracking-widest text-neutral-500 mb-1">Total Fees</p>
                        <p className="text-lg font-black font-mono text-yellow-400">{pnl.fees.toFixed(4)}</p>
                      </div>
                      <div className="bg-neutral-800/50 rounded-xl p-4">
                        <p className="text-[9px] font-black uppercase tracking-widest text-neutral-500 mb-1">Buy Value</p>
                        <p className="text-lg font-black font-mono text-white">${pnl.buyVal.toFixed(2)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Manual details display in Review */}
                  {(manualFields.primary_setup_type || manualFields.key_indicators || manualFields.remarks) && (
                    <div className="bg-neutral-900/20 border border-neutral-800 rounded-2xl p-5 space-y-3">
                      <p className="text-[10px] font-black uppercase tracking-widest text-neutral-500">Manual Analysis Details</p>
                      {manualFields.primary_setup_type && (
                        <p className="text-xs text-neutral-400"><span className="font-bold text-neutral-500">Setup:</span> {manualFields.primary_setup_type}</p>
                      )}
                      {manualFields.key_indicators && (
                        <p className="text-xs text-neutral-400"><span className="font-bold text-neutral-500">Indicators:</span> {manualFields.key_indicators}</p>
                      )}
                      {manualFields.session && (
                        <p className="text-xs text-neutral-400"><span className="font-bold text-neutral-500">Session:</span> {manualFields.session}</p>
                      )}
                      {manualFields.remarks && (
                        <p className="text-xs text-neutral-400"><span className="font-bold text-neutral-500">Remarks:</span> {manualFields.remarks}</p>
                      )}
                    </div>
                  )}

                  <div className="space-y-2">
                    <p className="text-[10px] font-black uppercase tracking-widest text-neutral-500">Executions</p>
                    {executions.map((exec, idx) => {
                      const acct = accounts.find((a: any) => a.id === exec.cex_account_id);
                      const cex = SUPPORTED_CEX.find(c => c.value.toLowerCase() === (acct?.cex_name || '').toLowerCase());
                      return (
                        <div key={exec.id} className="flex items-center justify-between bg-neutral-900/40 border border-neutral-800 rounded-xl px-4 py-3">
                          <div className="flex items-center gap-3">
                            <span className="text-[10px] text-neutral-600">#{idx + 1}</span>
                            {cex?.icon ? (
                              <img src={cex.icon} alt={cex.label} className="w-5 h-5 object-contain rounded" />
                            ) : (
                              <div className="w-5 h-5 bg-neutral-700 rounded" title="No icon" />
                            )}
                            <div>
                              <p className="text-xs font-bold text-white">{acct?.cex_display_name || 'Unknown'}</p>
                              <p className="text-[10px] text-neutral-500">{exec.trade_date.replace('T', ' ')}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`text-xs font-black font-mono ${exec.side === 'BUY' ? 'text-green-400' : 'text-red-400'}`}>{exec.side} {exec.qty} @ ${exec.price}</p>
                            <p className="text-[10px] text-neutral-500 font-mono">${((safeParseFloat(exec.price) || 0) * (safeParseFloat(exec.qty) || 0)).toFixed(2)}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}

            </AnimatePresence>

            {error && (
              <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
                className="mt-4 flex items-center gap-2 bg-red-900/20 border border-red-900/50 rounded-xl px-4 py-3">
                <AlertCircle size={14} className="text-red-400 flex-shrink-0" />
                <p className="text-xs text-red-400">{error}</p>
              </motion.div>
            )}
          </div>

          {/* Footer */}
          <div className="px-8 py-6 border-t border-neutral-800/60 bg-[#0A0A0A] flex justify-between items-center">
            <button onClick={step === 0 ? onClose : () => setStep(s => s - 1)}
              className="flex items-center gap-2 px-5 py-2.5 bg-neutral-900 border border-neutral-800 rounded-xl text-sm font-bold text-neutral-400 hover:text-white hover:bg-neutral-800 transition-all">
              <ChevronLeft size={16} /> {step === 0 ? 'Cancel' : 'Back'}
            </button>
            <div className="flex items-center gap-3">
              <div className="flex gap-1.5">
                {STEPS.map((_, i) => (
                  <div key={i} className={`h-1.5 rounded-full transition-all ${i === step ? 'w-6 bg-red-500' : i < step ? 'w-3 bg-green-500' : 'w-3 bg-neutral-700'}`} />
                ))}
              </div>
              {step < 3 ? (
                <button onClick={handleNext}
                  className="flex items-center gap-2 px-6 py-2.5 bg-red-600 hover:bg-red-500 rounded-xl text-sm font-black text-white transition-all shadow-lg shadow-red-900/30">
                  Next <ChevronRight size={16} />
                </button>
              ) : (
                <button onClick={handleSubmit} disabled={isSubmitting}
                  className="flex items-center gap-2 px-6 py-2.5 bg-green-600 hover:bg-green-500 rounded-xl text-sm font-black text-white transition-all shadow-lg shadow-green-900/30 disabled:opacity-50">
                  {isSubmitting ? <><Loader2 size={16} className="animate-spin" /> Saving...</> : <><Check size={16} /> Save Trade</>}
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default AddManualTradeWizard;
